import React from 'react';
import { useIntlBds } from '../../../../../translations/BdsIntlProvider';
import { Badge } from '../../../../Badge/Badge';
import { Box } from '../../../../Box/Box';
import { Button } from '../../../../Button';
import { Chip } from '../../../../Chip/Chip';
import { Drawer } from '../../../../Drawer/Drawer';
import { Grid } from '../../../../Grid/Grid';
import { Grow } from '../../../../Grow';
import { HighlightablePaper, useHighlightListItem } from '../../../../HighlightablePaper';
import { Icon } from '../../../../Icon/Icon';
import { IconButton } from '../../../../IconButton/IconButton';
import { Typography } from '../../../../Typography';
import { dataTableClassnames } from '../../const/classNames';
import { useDataTableContext } from '../../context/DataTableContext';
import { useFilterState } from '../../utils/useFilterState';
import { FilterElement } from './FilterElement';
import { DataTableTypes } from '../../../DataTableTypes';

export const Filters: React.FC = () => {
  const { intl, format } = useIntlBds();
  const { slots } = useDataTableContext();

  const {
    availableFilterDefinitions,
    activeFilters,
    count,
    queryModel,
    getQueryValueById,
    setQueryModel,
    clearQueryModel,
    clearQueryModelByKey,
    drawer
  } = useFilterState();

  const { addRef, highlight } = useHighlightListItem();

  const highlightFilter = (filterId: string) => {
    highlight(filterId);
  };

  const handleClose = () => {
    drawer.setOpen(false);
  };

  return (
    <>
      <Badge
        badgeContent={count.active}
        color="primary"
        overlap="circular"
        showZero
        sx={{ '.MuiBadge-badge': { pointerEvents: 'none' } }}
      >
        <IconButton
          dataTest={dataTableClassnames.filters.root}
          color={count.active > 0 ? 'active' : undefined}
          aria-label={intl.DataTable.Filters.filtersLabel}
          className={dataTableClassnames.filters.root}
          slotProps={{
            tooltip: {
              title: intl.DataTable.Filters.filtersLabel
            }
          }}
          iconName="filter_list"
          onClick={() => {
            drawer.setOpen(true);
          }}
        />
      </Badge>
      <Drawer open={drawer.open} anchor="right" onClose={handleClose}>
        <Box padding={6} minWidth={500} maxWidth={500}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid container justifyContent="space-between" alignItems="center" gap={4}>
              <Icon name="filter_list" />
              <Typography.Heading small>{intl.DataTable.Filters.filtersLabel}</Typography.Heading>
            </Grid>
            <Grid>
              <IconButton iconName="close" dataTest="close-columns-configuration-button" onClick={handleClose} />
            </Grid>
          </Grid>
          {slots?.filter?.drawer?.subheader}
          <Box paddingBlockStart={5}>
            <Grid container alignItems="center" justifyContent="space-between" marginBottom={5}>
              <Typography.Title>
                {format(intl.DataTable.Filters.ActiveAndAvailable.activeFilters, {
                  activeFiltersLength: activeFilters.length
                })}
              </Typography.Title>
              <Grid container spacing={2}>
                <Button color="primary" disabled={count.active === 0} onClick={clearQueryModel}>
                  {intl.DataTable.Filters.ActiveAndAvailable.clearAllFilters}
                </Button>
                {slots?.filter?.drawer?.activeFiltersActions}
              </Grid>
            </Grid>

            <Box>
              {activeFilters.length === 0 && (
                <Box minHeight={28}>
                  <Typography.Body color="colorOnSurfaceVariant">
                    {intl.DataTable.Filters.ActiveAndAvailable.noActiveFilters}
                  </Typography.Body>
                </Box>
              )}
              {availableFilterDefinitions.map((filter) => {
                const show = !!activeFilters.find((it) => it.id === filter.id);
                if (!show) {
                  return null;
                }
                return (
                  <Grow in key={filter.id} unmountOnExit style={{ transformOrigin: 'center' }}>
                    <Box padding={1} display="inline-block">
                      <Chip
                        size="small"
                        label={filter.label}
                        onClick={() => highlightFilter(filter.id)}
                        onDelete={() => clearQueryModelByKey(filter.id)}
                      />
                    </Box>
                  </Grow>
                );
              })}
            </Box>
          </Box>
          <Box paddingBlockStart={5}>
            <Typography.Title marginBottom={5}>
              {format(intl.DataTable.Filters.ActiveAndAvailable.availableFilters, {
                availableFiltersLength: count.available
              })}
            </Typography.Title>
            <Box>
              {availableFilterDefinitions.map((filter, index) => {
                if (filter.type === DataTableTypes.Filters.Type.hidden) {
                  return null;
                }
                return (
                  <HighlightablePaper
                    key={filter.id}
                    id={dataTableClassnames.filters.filterWrapper(filter.id)}
                    variant="outlined"
                    ref={(api) => addRef(index, filter.id, api)}
                    sx={{ padding: 3, marginBlock: 5 }}
                  >
                    <FilterElement
                      filter={filter}
                      getQueryValueById={getQueryValueById}
                      queryModel={queryModel}
                      setQueryModel={setQueryModel}
                    />
                  </HighlightablePaper>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
